import { useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { selectCurrentCommunity } from "state/communities/communitiesSlice";
import { useGetEMSMemberEnergyQuery } from "state/communities/communitiesApiSlice";
import { useGetMetersByCommunityIdQuery } from "state/meters/metersApiSlice";

import EmsChartCard from "./EmsChartCard";

/* Chart 5 — cost per member before vs. after the local market / optimization.
   Negative = profit (generates more than it consumes). */
function PromoterCostChart({ moduleType, startDatetime, endDatetime, onDataStatus }) {
  const { t } = useTranslation(["community"]);
  const community = useSelector(selectCurrentCommunity);
  const communityId = community?.id;

  const {
    data: rawList = [],
    isFetching,
    currentData,
  } = useGetEMSMemberEnergyQuery(
    { communityId, moduleType, startDatetime, endDatetime, limit: 5000, offset: 0 },
    { skip: !communityId }
  );
  // Pending for the *current* args (date range / module); a background
  // polling refetch keeps currentData and therefore does not flash a spinner.
  const isLoading = isFetching && currentData === undefined;
  const { data: metersData } = useGetMetersByCommunityIdQuery(
    { communityId },
    { skip: !communityId }
  );

  const cpeLabel = (cpe) => metersData?.entities?.[cpe]?.identifier || String(cpe).slice(0, 6);

  // c_ind / c_ind2bilateral are constant per CPE for a run — take first seen.
  const meterCosts = useMemo(() => {
    const seen = new Map();
    rawList.forEach((r) => {
      const cpe = String(r.cpe_id);
      if (seen.has(cpe)) return;
      const c1 = r.metadata?.c_ind;
      const c2 = r.metadata?.c_ind2bilateral;
      if (c1 != null || c2 != null) seen.set(cpe, { c_ind: c1, c_ind2bilateral: c2 });
    });
    return seen;
  }, [rawList]);

  const isEmpty = !isLoading && meterCosts.size === 0;
  useEffect(() => {
    if (onDataStatus) onDataStatus(isLoading ? null : !isEmpty);
  }, [isLoading, isEmpty, onDataStatus]);

  const { series, labels } = useMemo(() => {
    if (meterCosts.size === 0) return { series: [], labels: [] };
    const cpes = [...meterCosts.keys()].sort((a, b) => cpeLabel(a).localeCompare(cpeLabel(b)));
    return {
      labels: cpes.map((c) => cpeLabel(c)),
      series: [
        {
          name: t("unified_ems.promoter_cost_c_ind"),
          data: cpes.map((c) => {
            const v = meterCosts.get(c)?.c_ind;
            return v != null ? Number(Number(v).toFixed(2)) : null;
          }),
          color: "var(--ink-3)",
        },
        {
          name: t("unified_ems.promoter_cost_c_ind2bilateral"),
          data: cpes.map((c) => {
            const v = meterCosts.get(c)?.c_ind2bilateral;
            return v != null ? Number(Number(v).toFixed(2)) : null;
          }),
          color: "var(--accent)",
        },
      ],
    };
  }, [meterCosts, metersData, t]);

  if (isLoading || isEmpty) return null;
  const legend = series.map((s) => ({ label: s.name, color: s.color }));

  return (
    <EmsChartCard
      title={t("unified_ems.promoter_cost_chart_title")}
      sub={t("unified_ems.promoter_cost_chart_sub")}
      legend={legend}
      series={series}
      labels={labels}
      height={420}
      unit="€"
      yTitle="€"
      type="bar"
    />
  );
}

PromoterCostChart.defaultProps = {
  startDatetime: undefined,
  endDatetime: undefined,
  onDataStatus: undefined,
};
PromoterCostChart.propTypes = {
  moduleType: PropTypes.string.isRequired,
  startDatetime: PropTypes.string,
  endDatetime: PropTypes.string,
  onDataStatus: PropTypes.func,
};

export default PromoterCostChart;
